/** USPS UI transport and shared presentation helpers. Business rules stay in mcp-api. */
import { userAPI } from './supabase';
export const PACKAGE_TYPES = [
  ['priority_mail_express', 'Priority Mail Express'],
  ['priority_mail', 'Priority Mail'],
  ['ground_advantage', 'USPS Ground Advantage'],
  ['international', 'International'],
  ['returns', 'Returns'],
  ['other', 'Other (rides along)']
];
export const PICKUP_STATUS = {
  scheduled: 'Scheduled',
  pending: 'Waiting for USPS',
  changed_outside: 'Changed outside OpSyncPro',
  cancelled: 'Cancelled',
  date_passed: 'Date passed',
  // Not a server status: a scheduled pickup whose 2:00 AM CT cutoff has passed (TC31).
  locked: 'Locked'
};
export const CUTOFF_NOTE = 'Changes for a pickup day close at 2:00 AM CT that morning.';

/** Plain names for the server's address and pickup field keys, so no raw key reaches the UI. */
export const FIELD_LABELS = {
  label: 'Label',
  street_address: 'Street address',
  secondary_address: 'Apartment or suite',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  zip_plus4: 'ZIP+4',
  urbanization: 'Urbanization',
  email: 'Contact email',
  phone: 'Contact cell',
  first_name: 'First name',
  last_name: 'Last name',
  pickup_date: 'Pickup day',
  estimated_weight_lb: 'Weight (lb)',
  package_location: 'Where to leave',
  special_instructions: 'Instructions',
  dog_present: 'Dog present',
  tracking_numbers: 'Labels'
};

/** Plain wording for carrier instructions with a character USPS refuses (same text as mcp-api). */
export const INSTRUCTIONS_CHARSET_MESSAGE = "Use only letters, numbers, spaces and . , - ' # / in the instructions.";
// Mirrors mcp-api's rule (src/lib/usps-pickup.js, OPS-1406 N12): USPS refuses some
// symbols in pickup instructions at booking (a colon is proven refused), so only
// letters, numbers, spaces and . , - ' # / are allowed. Line breaks count as spaces
// (mcp-api collapses whitespace before it checks).
const INSTRUCTIONS_ALLOWED = /^[A-Za-z0-9\s.,\-'#/]*$/;

/**
 * The plain error for carrier instructions USPS would refuse, or null when the
 * text is fine (blank included). Checked before any request so the form can
 * show it inline under the instructions box.
 */
export function instructionsError(text) {
  if (!text || INSTRUCTIONS_ALLOWED.test(text)) {
    return null;
  }
  return INSTRUCTIONS_CHARSET_MESSAGE;
}

/** Plain name for a field key; unknown keys lose their underscores and get a capital letter. */
export function fieldLabel(key) {
  if (FIELD_LABELS[key]) {
    return FIELD_LABELS[key];
  }
  const words = String(key).replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "1 package", "2 packages". `plural` defaults to the noun plus "s". */
export function pluralize(count, noun, plural = `${noun}s`) {
  return `${count} ${Number(count) === 1 ? noun : plural}`;
}

/** The badge to show for a pickup: a scheduled pickup past its cutoff shows as Locked. */
export function displayStatus(pickup = {}) {
  if (pickup.status === 'scheduled' && pickup.can_change === false) {
    return 'locked';
  }
  return pickup.status;
}

/**
 * Whether a preview row is a real change. A missing dog_present equals false,
 * emails compare case-insensitively (USPS upper-cases them), anything else by value.
 */
export function isRealChange(key, from, to) {
  if (key === 'dog_present') {
    return Boolean(from) !== Boolean(to);
  }
  if (key === 'email' && typeof from === 'string' && typeof to === 'string') {
    return from.trim().toLowerCase() !== to.trim().toLowerCase();
  }
  return JSON.stringify(from ?? null) !== JSON.stringify(to ?? null);
}

// A server error with no code of its own (e.g. a bare 500 "Internal Server Error") is
// shown as this. The outcome of a write is not known, so it locks like a lost connection.
const SERVICE_ERROR = {
  success: false,
  code: 'connection_unavailable',
  error: 'The pickup service had a problem, so the outcome is not known. Refresh pickups before making ' +
    'another change.'
};
/** Call the authenticated shipping proxy. */
export async function pickupFetch(path, body) {
  const addressTool = [
    'shipping_check_usps_pickup_eligibility', 'shipping_save_pickup_address',
    'shipping_archive_pickup_address', 'shipping_set_default_pickup_address'
  ].includes(body?.tool);
  const serviceError = addressTool ? {
    success: false,
    code: 'connection_unavailable',
    error: 'The address service had a problem. Try again in a moment.'
  } : SERVICE_ERROR;
  try {
    const token = await userAPI.getAuthToken();
    const response = await fetch(`/.netlify/functions/shipping-pickups-proxy?path=${encodeURIComponent(path)}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    if (response.status >= 500) {
      // Keep a coded server answer (e.g. usps_unavailable); replace a bare server error with plain copy.
      const data = await response.json().catch(() => null);
      return data?.code ? data : { ...serviceError };
    }
    return await response.json();
  } catch {
    if (addressTool) {
      return { ...serviceError };
    }
    return {
      success: false,
      code: 'connection_unavailable',
      error: 'Could not reach the pickup service. The request outcome is not known. Refresh pickups ' +
        'before making another change.'
    };
  }
}
/** Dispatch an allowed pickup tool. */
export const pickupTool = (tool, params = {}) => pickupFetch('/execute', {
  tool,
  params
});
/** Read service days for a date range. */
export const pickupCalendar = (from, to) => pickupFetch(`/calendar?from=${from}&to=${to}`);

/** Use the saved USPS representation consistently, falling back to the ledger snapshot. */
export function addressString(address = {}, pickup = {}) {
  // Existing bookings retain their USPS snapshot after a saved street is edited.
  if (pickup.address_display) {
    return pickup.address_display;
  }
  const postal = pickup.address || address;
  const label = address.label || pickup.address_label || postal.label;
  const street = [postal.street_address, postal.secondary_address].filter(Boolean).join(' ');
  const heading = [label, street].filter(Boolean).join(' ');
  return [heading, [postal.city, postal.state].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
}
/** Format a pickup day in Chicago time. */
export function pickupDate(date, long = false) {
  if (!date) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    weekday: long ? 'long' : 'short',
    month: long ? 'long' : 'short',
    day: 'numeric'
  }).format(new Date(`${date}T12:00:00Z`));
}
/** Describe the package breakdown. */
export function packageSummary(packages = {}) {
  return PACKAGE_TYPES.filter(([key]) => packages?.[key]).map(([key, name]) => `${name} ${packages[key]}`).join(' · ');
}
/** Count all packages in a pickup. */
export const packageCount = packages => Object.values(packages || {}).reduce((sum, count) => sum + Number(count), 0);
/** Match Integrations and its child routes. */
export const isIntegrationsPath = path => path === '/integrations' || path.startsWith('/integrations/');
